import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { teamService, ITeam } from './service/team.service';
import { UpgradeModal } from '../common/components/upgrade-modal.component';

export const TeamsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = React.useState(false);
  const [upgradeMessage, setUpgradeMessage] = React.useState<string | null>(null);

  const { data: teams, isLoading } = useQuery({
    queryKey: ['my-teams'],
    queryFn: () => teamService.getMyTeams(),
    staleTime: 0
  });

  const { register, handleSubmit, reset } = useForm<{ name: string }>();

  const { mutate: createTeam, isPending } = useMutation({
    mutationFn: (name: string) => teamService.createTeam(name),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['my-teams'] });
        reset();
        setShowCreate(false);
        navigate(`/team/${data.id}`);
      }
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      if (err.response?.status === 403 && msg) {
        setUpgradeMessage(msg);
      } else {
        toast.error(msg || t('teams.createTeamFailed'));
      }
    }
  });

  return (
    <div className="max-w-[700px] mx-auto py-[32px] md:py-[48px] px-[16px]">
      <div className="mb-[24px]">
        <h1 className="font-display text-[28px] font-bold text-base-black dark:text-white mb-[12px]">
          {t('teams.myTeams')}
        </h1>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="h-[36px] px-[16px] rounded-[32px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all"
        >
          {showCreate ? t('common.cancel') : t('teams.createTeam')}
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={handleSubmit((d) => createTeam(d.name))}
          className="flex gap-[8px] mb-[16px]"
        >
          <input
            placeholder={t('teams.teamName')}
            {...register('name', { required: true })}
            className="flex-1 h-[44px] px-[16px] text-[16px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
          />
          <button
            type="submit"
            disabled={isPending}
            className="h-[44px] px-[20px] rounded-[32px] font-body text-[14px] font-medium text-white dark:text-[#1C1C1F] bg-base-black dark:bg-[#C8C8CB] hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all disabled:opacity-50"
          >
            {isPending ? t('dashboard.creating') : t('common.create')}
          </button>
        </form>
      )}

      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[16px] md:p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)]">
      {isLoading ? (
        <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">{t('common.loading')}</p>
      ) : (teams as ITeam[])?.length > 0 ? (
        <div className="flex flex-col gap-[8px]">
          {(teams as ITeam[]).map((team) => (
            <div
              key={team.id}
              onClick={() => navigate(`/team/${team.id}`)}
              className="flex items-center justify-between p-[16px] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-white dark:bg-[#28282B] shadow-[0_1px_3px_rgba(0,0,0,0.07)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:-translate-y-[1px] transition-all cursor-pointer"
            >
              <div>
                <p className="font-body text-[16px] font-semibold text-[#111827] dark:text-white">
                  {team.name}
                </p>
                <p className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3]">
                  {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}{' '}
                  &middot; {team.reviewCount} review
                  {team.reviewCount !== 1 ? 's' : ''}
                </p>
              </div>
              <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#EFEFF1] dark:bg-[#333336] text-[#4A4A4C] dark:text-[#A0A0A3] uppercase">
                {team.role === 'admin' ? t('common.admin') : t('common.memberRole')}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] text-center py-[32px]">
          {t('teams.noTeams')}
        </p>
      )}
      </div>

      {upgradeMessage && (
        <UpgradeModal message={upgradeMessage} onClose={() => setUpgradeMessage(null)} />
      )}
    </div>
  );
};
